import React from "react";
import PropTypes from "prop-types";
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

import I18n from "../i18n.js";
import MoneyFormatter from "../../utils/money_formatter.js";
import PercentFormatter from "../../utils/percent_formatter.js";

import ProgressBar from "../components/progress_bar.js";

const blockStyles = makeStyles(theme => ({
  root: {
    padding: "0px 32px",
    fontWeight: theme.typography.fontWeightMedium,
    ['@media (max-width: 768px)']: {
      padding: "0px 18px",
    },
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: "24px 0",
    ['@media (max-width: 768px)']: {
      padding: "14px 0px 12px",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    "&:first-child": {
      textAlign: "left",
    },
    "&:last-child": {
      textAlign: "right",
    },
  },
  value: {
    fontSize: "28px",
    lineHeight: "1.3",
    margin: "0 0 12px",
    ['@media (max-width: 768px)']: {
      fontSize: "16px",
      lineHeight: "1.375",
      margin: "0px",
    },
  },
  label: {
    fontSize: "18px",
    lineHeight: "1.3",
    margin: "0",
    textTransform: "capitalize",
    ['@media (max-width: 768px)']: {
      fontSize: "12px",
      lineHeight: "16px",
    },
  },
  progressRow: {
    width: "100%",
    marginBottom: "10px",
    ['@media (max-width: 768px)']: {
      marginBottom: "-6px",
    },
  },
  funded: {
    fontSize: "22px",
    letterSpacing: "0.5px",
    textTransform: "capitalize",
    marginBottom: "10px",
    ['@media (max-width: 768px)']: {
      fontSize: "13px",
      lineHeight: "16px",
      marginBottom: "5px",
    },
  }
}));

/**
 * Display info of project about collected investments. Also displays progress bar
 * @name ProjectsInvestmentsInfo
 * @param {object} props React component properties
 * @param {string} [props.className] CSS-class of container
 * @param {object} [props.project] Project info from API
 * @example
 * const project = projects[0];
 * return (
 *  <ProjectsInvestmentInfo className = { "class-name" } 
 *                          project = { project } />
 * )
*/

const ProjectsInvestmentsInfo =  (props) => {

  const classes = blockStyles();
  const { className, project = {} } = props;
  
  const cost                = parseFloat(project.cost || "0")
  const amountOfInvestments = parseFloat(project.amountOfInvestments || "0")
  const amountOfOwnCapital  = parseFloat(project.amountOfOwnCapital || "0")
  const amountOfBankCredit  = parseFloat(project.amountOfBankCredit || "0")
  
  const needInvestments = cost - amountOfOwnCapital - amountOfBankCredit;

  return (
    <div className = { clsx(classes.root, className) } >
      <div className = { classes.row } >
        <div className = { classes.column } >
          <div className = { classes.value }>
            { MoneyFormatter.format(amountOfInvestments) }
            &nbsp;
            { I18n.t("currency.euro") }
          </div>
          <div className = { classes.label }>
            { I18n.t("shared.raised_funds") }
          </div>   
        </div>
        <div className = { classes.column } >
          <div className = { classes.value }>
            { MoneyFormatter.format(needInvestments) }
            &nbsp;
            { I18n.t("currency.euro")}
          </div>
          <div className = { classes.label }>
            { I18n.t("shared.required_amount") }
          </div>   
        </div>
      </div>
      <div className = { classes.progressRow } >
        <ProgressBar curValue = { amountOfInvestments } maxValue = { needInvestments }/>
      </div>
      <div className = { classes.funded } >
        { `${PercentFormatter.format(amountOfInvestments / needInvestments, "0%")} ${I18n.t("shared.funded")}` }
      </div>
    </div>
  );
}

ProjectsInvestmentsInfo.propTypes = {
  project: PropTypes.object
}

export default ProjectsInvestmentsInfo;